import { Keyboard } from "@capacitor/keyboard";
import {
  IonAlert,
  IonButton,
  IonCol,
  IonContent,
  IonInput,
  IonItem,
  IonLabel,
  IonRow,
  IonSpinner,
} from "@ionic/react";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { checkIfEmailExists } from "@src/appV2/User/api/checkUserExists";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import validator from "validator";

import { updateProfile } from "./api";
import { PROFILE_UPDATE_LOCAL_STORAGE_ITEMS } from "./constants";
import { USER_EVENTS } from "../../../constants";
import { logout, reloadAgentProfile } from "../../store/session";

const INVALID_EMAIL_ENTERED = "Please enter a valid email address";
interface ProfileUpdateStepProps {
  stepFinished: () => void;
}

export function UpdateEmail(props: ProfileUpdateStepProps) {
  const { stepFinished } = props;
  const { showErrorToast } = useToast();
  const worker = useDefinedWorker();
  const [updatingData, setUpdatingData] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

  const dispatch = useDispatch();
  const captchaRef = useRef<HTMLDivElement | null>(null);
  const [doesEmailAlreadyExist, setDoesEmailAlreadyExist] = useState(false);

  useEffect(() => {
    if (worker.email) {
      setEmail(worker.email);
    }
  }, [worker]);

  async function initiateRecapcha() {
    await getFirebaseSingleton().initRecaptchaVerifier("recaptcha-container", captchaRef.current);
  }
  async function showErrorAndInitiateRecapcha(errorMessage: string) {
    showErrorToast(errorMessage);
    await initiateRecapcha();
  }

  const onNext = async (event?: FormEvent | MouseEvent) => {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    event && event.preventDefault();
    if (updatingData) {
      return;
    }
    if (isCapacitorPlatform()) {
      Keyboard.hide();
    }
    try {
      setUpdatingData(true);
      const isValidEmail = validator.isEmail(email);
      if (!isValidEmail) {
        setUpdatingData(false);
        await showErrorAndInitiateRecapcha(INVALID_EMAIL_ENTERED);
        return;
      }
      /**
       * An endpoint that provides anyone a way to check for existing emails is a
       * potential security risk. A hacker can use it to do an enumeration attack.
       */
      const { exists } = await checkIfEmailExists(email);
      if (!email) {
        setUpdatingData(false);
        await showErrorAndInitiateRecapcha("Please enter your email address");
        return;
      }
      if (exists) {
        setUpdatingData(false);
        setDoesEmailAlreadyExist(true);
        await initiateRecapcha();
        return;
      }

      setUpdatingData(false);
      setShowConfirmationModal(true);

      logEvent(USER_EVENTS.PROFILE_CHANGE_COMPLETED, {
        type: "EMAIL",
      });
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setUpdatingData(false);
      await showErrorAndInitiateRecapcha("Error occurred while updating email address.");
    } finally {
      setUpdatingData(false);
    }
  };

  const onProcessed = async () => {
    try {
      setUpdatingData(true);
      await updateProfile({ email });
      localStorage.setItem(PROFILE_UPDATE_LOCAL_STORAGE_ITEMS.UPDATE_PROFILE_EMAIL_CHANGED, "true");
      dispatch(reloadAgentProfile(false));
      setUpdatingData(false);
      stepFinished();
      dispatch(logout());
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setUpdatingData(false);
      await showErrorAndInitiateRecapcha("Error occurred while checking email address.");
    }
  };

  const closeModal = () => {
    setShowConfirmationModal(false);
  };

  const onEmailChange = (event: CustomEvent) => {
    setEmail(event.target?.["value"] || event.detail.value);
  };

  return (
    <IonContent>
      <IonAlert
        mode="ios"
        isOpen={doesEmailAlreadyExist}
        backdropDismiss={false}
        message="The email you tried to submit is already in use by another account. Please make sure you are submitting an email you own and that you typed it correctly."
        header="This email is already in use"
        buttons={[
          {
            text: "Try Again",
            handler: () => {
              setDoesEmailAlreadyExist(false);
            },
          },
        ]}
      />
      <IonAlert
        mode="ios"
        isOpen={showConfirmationModal}
        backdropDismiss={false}
        header="You need to login again"
        message="To validate your new email, you will need to logout and login again. This step is necessary for us to proper configure your account."
        buttons={[
          {
            text: "Cancel",
            handler: closeModal,
          },
          {
            text: "Confirm",
            handler: onProcessed,
          },
        ]}
      />

      <div className="signup-content content-layout adjust-form-height">
        <div className="form-container">
          <form onSubmit={onNext}>
            <IonRow>
              <IonCol>
                <div className="form-heading">
                  <h4>What&#39;s your new email?</h4>
                </div>

                <div>
                  <IonLabel className="form-label">Email Address</IonLabel>
                  <IonItem lines="none" className="form-item-wrapper">
                    <IonInput
                      name="email"
                      className="form-input"
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onIonChange={onEmailChange}
                      disabled={updatingData}
                      autofocus
                      required
                    />
                  </IonItem>
                </div>
              </IonCol>
            </IonRow>
          </form>
        </div>

        <div className="signupform-footer footer-container">
          <IonButton
            expand="block"
            size="large"
            class="ion-margin-top ion-margin-bottom continue-button"
            disabled={updatingData || worker.email === email || !email}
            onClick={onNext}
          >
            Continue
            {updatingData && <IonSpinner slot="end" class="ion-margin-start" name="lines" />}
          </IonButton>
          <div ref={captchaRef}></div>
        </div>
      </div>
    </IonContent>
  );
}
